import React from 'react'
import { Section } from './AddNewPatient'
import {
  Autocomplete,
  Box,
  createFilterOptions,
  Grid,
  TextField,
} from '@mui/material'
import { riskFlagOptions } from './clinicalRiskFlags'
import pmhxOptions from '../../patientRecords/ePCR/PMHx/PMHxOptions'
import commonPrescribedMedicinesUK from '../../patientRecords/ePCR/PMHx/drugs'
import commonAllergies from '../../patientRecords/ePCR/PMHx/allergies'

const findColor = (label) => {
  for (const g of riskFlagOptions) {
    const match = g.options.find((o) => o === label)
    if (match) return g.color
  }
  return '#1F3D7C'
}

const Clinical = ({
  clinicalNotes,
  setClinicalNotes,
  riskFlags,
  setRiskFlags,
  allergies,
  setAllergies,
  medications,
  setMedications,
  pmhx,
  setPmhx,
  existingPatient,
  setChangesMade,
}) => {
  const filter = createFilterOptions()
  return (
    <Section title="Clinical / Risk Factors">
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={riskFlagOptions.flatMap((g) =>
            g.options.map((label) => ({
              group: g.group,
              label,
              color: g.color,
            }))
          )}
          groupBy={(option) => option.group}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option
            if (option.label?.startsWith('Add ')) return option.label // show “Add ‘xxx’” in dropdown
            if (option.inputValue) return option.inputValue // show raw value when selected
            return option.label
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, onDelete, ...tagProps } = getTagProps({ index })
              const color = option.color || findColor(option.label || option)

              return (
                <Box
                  key={key}
                  {...tagProps}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    backgroundColor: `${color}15`,
                    border: `1px solid ${color}40`,
                    borderRadius: '16px',
                    padding: '2px 8px',
                    color: color,
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${color}25`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: color,
                    }}
                  />
                  {option.label || option}
                  {onDelete && (
                    <Box
                      component="span"
                      onClick={onDelete}
                      sx={{
                        ml: 0.5,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        color: color,
                        opacity: 0.6,
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      ×
                    </Box>
                  )}
                </Box>
              )
            })
          }
          renderOption={(props, option) => {
            const { key, ...rest } = props
            return (
              <li key={key} {...rest}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: option.color,
                    mr: 1,
                  }}
                />
                {option.label}
              </li>
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Risk Flags"
              placeholder={
                riskFlags?.length === 0 ? 'Select or add risk flags' : ''
              }
              helperText="Start typing to add a custom risk flag and then press 'enter'"
            />
          )}
          freeSolo
          onChange={(e, newValue) =>
            setRiskFlags(newValue.map((v) => v.label || v))
          }
          onBlur={() =>
            existingPatient &&
            riskFlags !== existingPatient?.riskFlags &&
            setChangesMade((prev) => ({
              ...prev,
              'Clinical Risks': {
                field: 'riskFlags',
                value: riskFlags,
              },
            }))
          }
          value={riskFlags}
          clearOnBlur={false}
          selectOnFocus
          handleHomeEndKeys
          filterOptions={(options, params) => {
            const filtered = filter(options, params)
            const { inputValue } = params

            const isExisting = options.some(
              (option) => option.label === inputValue
            )
            if (inputValue !== '' && !isExisting) {
              filtered.push({
                inputValue,
                label: `Add "${inputValue}"`,
                group: 'Custom Entry',
              })
            }

            return filtered
          }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          value={allergies}
          onChange={(e, newValue) => {
            setAllergies(newValue)
            if (newValue?.length > 0 && !riskFlags.includes('Allergy Alert')) {
              setRiskFlags((prev) => [...prev, 'Allergy Alert'])
            }
          }}
          fullWidth
          options={commonAllergies}
          freeSolo
          multiple
          renderInput={(params) => (
            <TextField
              {...params}
              label="Allergies"
              placeholder={
                riskFlags?.length === 0 ? 'Select or add Allergies' : ''
              }
              helperText="Start typing to add a custom allergy and then press 'enter'"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          value={medications}
          onChange={(e, newValue) => {
            setMedications(newValue)
          }}
          fullWidth
          options={commonPrescribedMedicinesUK}
          freeSolo
          multiple
          renderInput={(params) => (
            <TextField
              {...params}
              label="Medications"
              helperText="Start typing to add a custom medicine and then press 'enter'"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          value={pmhx}
          onChange={(e, newValue) => {
            setPmhx(
              newValue.reduce((accumulator, x) => {
                if (typeof x === 'object') {
                  accumulator.push(x)
                } else {
                  accumulator.push({
                    category: 'custom',
                    disease: x,
                  })
                }
                return accumulator
              }, [])
            )
          }}
          fullWidth
          options={pmhxOptions}
          freeSolo
          multiple
          getOptionLabel={(params) => params.disease}
          groupBy={(params) => params.category}
          onBlur={() =>
            existingPatient &&
            existingPatient?.pmhx !== pmhx &&
            setChangesMade((prev) => ({
              ...prev,
              'Past Medical History': {
                field: 'pmhx',
                value: pmhx,
              },
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Past Medical History"
              helperText="Start typing to add a custom condition and then press 'enter'"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          multiline
          minRows={2}
          value={clinicalNotes}
          label="Notes"
          onChange={(e) => setClinicalNotes(e.target.value)}
          onBlur={() =>
            existingPatient &&
            clinicalNotes !== existingPatient?.clinicalNotes &&
            setChangesMade((prev) => ({
              ...prev,
              'Clinical Notes': {
                field: 'clinicalNotes',
                value: clinicalNotes,
                objectRef: 'disease',
              },
            }))
          }
          autoComplete="none"
          fullWidth
        />
      </Grid>
    </Section>
  )
}

export default Clinical
